import { AdvancedOptions } from "./search/AdvancedOptions";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  apiService,
  ReassignError,
  type Lesson,
  type LessonDetail,
  type LessonExport,
  type LessonsPage,
  type ReassignLessonResponse,
  type SlideInfo,
  type Teacher,
  type TutorVersion,
  type TutorVersionAssignment,
} from "../services/api.service";
import { useHeaderActions } from "../context/HeaderActionsContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TablePagination } from "@/components/ui/pagination";
import { Skeleton } from "@/components/ui/skeleton";
import { LessonActions } from "./lesson-actions";
import {
  Loader2,
  Search,
  Copy,
  Trash2,
  UserRoundCog,
  UserPlus,
  ArrowLeft,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import {
  buildLessonConfigSearchParams,
  LESSON_ID_PARAM,
  parseLessonIdParam,
} from "./lesson-config/url-state";
import { LessonStandardsCard } from "./lesson-config/LessonStandardsCard";
import {
  formatSlideStatusLabel,
  getSlidePreviewImages,
  getSlidePreviewBlocks,
} from "./lesson-config/preview-formatters";
import {
  buildScratchpadMetadataPatch,
  isScratchpadEnabled,
} from "./lesson-config/scratchpad";
import {
  buildCalculatorMetadataPatch,
  isCalculatorEnabled,
} from "./lesson-config/calculator";

const PAGE_SIZE = 30;

export const LessonConfigAdmin = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // List view state
  const [lessonsPage, setLessonsPage] = useState<LessonsPage | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [teacherFilter, setTeacherFilter] = useState("all");
  const [organizationFilter, setOrganizationFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "published" | "draft">("all");
  const [lessonFilterTeachers, setLessonFilterTeachers] = useState<Teacher[]>([]);
  const [page, setPage] = useState(1);
  const [listLoading, setListLoading] = useState(false);

  // Detail view state
  const [lesson, setLesson] = useState<LessonDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [savingSlideId, setSavingSlideId] = useState<number | null>(null);
  const [isBulkSaving, setIsBulkSaving] = useState(false);
  const [savingCalculatorSlideId, setSavingCalculatorSlideId] = useState<number | null>(null);
  const [isBulkCalculatorSaving, setIsBulkCalculatorSaving] = useState(false);

  // Dialog state
  const [cloneOpen, setCloneOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Clone/Reassign teacher search
  const [teacherQuery, setTeacherQuery] = useState("");
  const [teacherResults, setTeacherResults] = useState<Teacher[]>([]);
  const [selectedTeacher, setSelectedTeacher] = useState<Teacher | null>(null);
  const [crossOrgPrompt, setCrossOrgPrompt] = useState<{
    sourceOrg: string | null;
    targetOrg: string | null;
  } | null>(null);
  const [teacherSearchLoading, setTeacherSearchLoading] = useState(false);
  const teacherDebounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Preview/Export
  const [exportData, setExportData] = useState<LessonExport | null>(null);
  const [exportLoading, setExportLoading] = useState(false);

  // Tutor version binding
  const [availableTutorVersions, setAvailableTutorVersions] = useState<TutorVersion[]>([]);
  const [tvConfirmOpen, setTvConfirmOpen] = useState(false);
  const [pendingTutorVersionId, setPendingTutorVersionId] = useState<string | null>(null);
  const [allOrgs, setAllOrgs] = useState<{ id: string; name: string }[]>([]);
  const orgIdToName = useMemo(() => {
    const map: Record<string, string> = {};
    for (const org of allOrgs) map[org.id] = org.name;
    return map;
  }, [allOrgs]);
  const [currentAssignment, setCurrentAssignment] = useState<TutorVersionAssignment | null>(null);
  const [tvBindingLoading, setTvBindingLoading] = useState(false);

  const listRequestRef = useRef(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const { setActions } = useHeaderActions();
  const rawSelectedLessonId = searchParams.get(LESSON_ID_PARAM);
  const selectedLessonId = parseLessonIdParam(rawSelectedLessonId);

  useEffect(() => {
    setActions(null);
    return () => setActions(null);
  }, [setActions]);

  const setSelectedLessonIdInUrl = useCallback(
    (lessonId: number | null) => {
      setSearchParams((current) =>
        buildLessonConfigSearchParams(current, lessonId),
      );
    },
    [setSearchParams],
  );

  // Fetch lessons list
  const fetchLessons = useCallback(async (
    search: string,
    pg: number,
    selectedTeacherId: string,
    selectedStatus: "all" | "published" | "draft",
  ) => {
    const requestId = ++listRequestRef.current;
    try {
      setListLoading(true);
      const data = await apiService.getLessonsPage({
        search: search || undefined,
        teacherId:
          selectedTeacherId !== "all"
            ? Number.parseInt(selectedTeacherId, 10)
            : undefined,
        status: selectedStatus === "all" ? undefined : selectedStatus,
        organizationId: organizationFilter || undefined,
        limit: PAGE_SIZE,
        offset: (pg - 1) * PAGE_SIZE,
      });
      if (requestId === listRequestRef.current) setLessonsPage(data);
    } catch {
      if (requestId === listRequestRef.current) toast.error("Failed to load lessons");
    } finally {
      if (requestId === listRequestRef.current) setListLoading(false);
    }
  }, [organizationFilter]);

  // Load teacher filter options
  useEffect(() => {
    const loadTeachers = async () => {
      try {
        const data = await apiService.getTeachersForFilter();
        setLessonFilterTeachers(data);
      } catch {
        setLessonFilterTeachers([]);
      }
    };
    loadTeachers();
    apiService.getOrganizations().then(setAllOrgs).catch(() => {});
  }, []);

  // Load on mount and when page changes
  useEffect(() => {
    fetchLessons(searchQuery, page, teacherFilter, statusFilter);
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      fetchLessons(searchQuery, 1, teacherFilter, statusFilter);
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setPage(1);
    fetchLessons(searchQuery, 1, teacherFilter, statusFilter);
  }, [teacherFilter, statusFilter, organizationFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadLessonDetail = useCallback(async (lessonId: number) => {
    try {
      setIsLoading(true);
      setLesson(null);
      const data = await apiService.getLessonSlides(lessonId);
      setLesson(data);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to load lesson");
      setLesson(null);
      setSelectedLessonIdInUrl(null);
    } finally {
      setIsLoading(false);
    }
  }, [setSelectedLessonIdInUrl]);

  // Load tutor versions and assignment when lesson detail is loaded
  useEffect(() => {
    if (lesson) {
      apiService.getTutorVersions("ai-tutor").then(setAvailableTutorVersions).catch((error) => toast.error(error instanceof Error ? error.message : "Failed to load tutor versions"));
      apiService.getOrganizations().then(setAllOrgs).catch((error) => toast.error(error instanceof Error ? error.message : "Failed to load organizations"));
      apiService.getAssignment("ai-tutor", lesson.lesson_id.toString()).then(setCurrentAssignment).catch((error) => { setCurrentAssignment(null); toast.error(error instanceof Error ? error.message : "Failed to load lesson assignment"); });
    } else {
      setCurrentAssignment(null);
    }
  }, [lesson?.lesson_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentTutorVersionId = currentAssignment?.tutor_version_id;

  const handleBindTutorVersion = async (tutorVersionId: string | null) => {
    if (!lesson) return;
    setTvBindingLoading(true);
    try {
      const lessonIdStr = lesson.lesson_id.toString();
      if (tutorVersionId) {
        const assignment = await apiService.setAssignment("ai-tutor", lessonIdStr, tutorVersionId, "dashboard-admin");
        setCurrentAssignment(assignment);
      } else {
        await apiService.removeAssignment("ai-tutor", lessonIdStr);
        setCurrentAssignment(null);
      }
      toast.success(tutorVersionId ? "Tutor version assigned" : "Tutor version unassigned");
    } catch {
      toast.error("Failed to update tutor version binding");
    } finally {
      setTvBindingLoading(false);
    }
  };

  useEffect(() => {
    if (rawSelectedLessonId && selectedLessonId == null) {
      setSelectedLessonIdInUrl(null);
      return;
    }

    if (selectedLessonId == null) {
      setLesson((currentLesson) => (currentLesson ? null : currentLesson));
      return;
    }

    if (lesson?.lesson_id === selectedLessonId) {
      return;
    }

    loadLessonDetail(selectedLessonId);
  }, [
    lesson?.lesson_id,
    loadLessonDetail,
    rawSelectedLessonId,
    selectedLessonId,
    setSelectedLessonIdInUrl,
  ]);

  const handleSelectLesson = (selected: Lesson) => {
    setSelectedLessonIdInUrl(selected.lesson_id);
  };

  const handleBackToList = () => {
    setSelectedLessonIdInUrl(null);
    setLesson(null);
    // Refresh the list in case of deletions/clones
    fetchLessons(searchQuery, page, teacherFilter, statusFilter);
  };

  const handleToggleScratchpad = async (
    slide: SlideInfo,
    enabled: boolean,
  ) => {
    if (!lesson) return;
    try {
      setSavingSlideId(slide.id);
      const updatedSlide = await apiService.updateSlideMetadata(
        lesson.lesson_id,
        slide.id,
        buildScratchpadMetadataPatch(enabled),
      );
      setLesson((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          slides: prev.slides.map((s) =>
            s.id === slide.id
              ? {
                  ...s,
                  metadata: updatedSlide.metadata,
                }
              : s,
          ),
        };
      });
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to update scratchpad setting",
      );
    } finally {
      setSavingSlideId(null);
    }
  };

  const handleToggleAll = async (enableAll: boolean) => {
    if (!lesson) return;
    try {
      setIsBulkSaving(true);
      const slideIds = lesson.slides.map((slide) => slide.id);
      const result = await apiService.updateSlidesMetadata(
        lesson.lesson_id,
        slideIds,
        buildScratchpadMetadataPatch(enableAll),
      );
      const metadataBySlideId = new Map(
        result.slides.map((slide) => [slide.id, slide.metadata]),
      );
      setLesson((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          slides: prev.slides.map((s) => {
            const savedMetadata = metadataBySlideId.get(s.id);
            return savedMetadata
              ? {
                  ...s,
                  metadata: savedMetadata,
                }
              : s;
          }),
        };
      });

      if (result.updated_count === slideIds.length) {
        toast.success(
          `Scratchpad ${enableAll ? "enabled" : "disabled"} for all ${slideIds.length} slides`,
        );
        return;
      }

      toast.warning(
        `Scratchpad ${enableAll ? "enabled" : "disabled"} on ${result.updated_count} of ${slideIds.length} slides. Please retry the remaining slides.`,
      );
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to update all slides",
      );
    } finally {
      setIsBulkSaving(false);
    }
  };

  const handleToggleCalculator = async (
    slide: SlideInfo,
    enabled: boolean,
  ) => {
    if (!lesson) return;
    try {
      setSavingCalculatorSlideId(slide.id);
      const updatedSlide = await apiService.updateSlideMetadata(
        lesson.lesson_id,
        slide.id,
        buildCalculatorMetadataPatch(enabled),
      );
      setLesson((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          slides: prev.slides.map((s) =>
            s.id === slide.id ? { ...s, metadata: updatedSlide.metadata } : s,
          ),
        };
      });
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to update calculator setting",
      );
    } finally {
      setSavingCalculatorSlideId(null);
    }
  };

  const handleToggleAllCalculator = async (enableAll: boolean) => {
    if (!lesson) return;
    try {
      setIsBulkCalculatorSaving(true);
      const slideIds = lesson.slides.map((slide) => slide.id);
      const result = await apiService.updateSlidesMetadata(
        lesson.lesson_id,
        slideIds,
        buildCalculatorMetadataPatch(enableAll),
      );
      const metadataBySlideId = new Map(
        result.slides.map((slide) => [slide.id, slide.metadata]),
      );
      setLesson((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          slides: prev.slides.map((s) => {
            const savedMetadata = metadataBySlideId.get(s.id);
            return savedMetadata ? { ...s, metadata: savedMetadata } : s;
          }),
        };
      });

      if (result.updated_count === slideIds.length) {
        toast.success(
          `Calculator ${enableAll ? "enabled" : "disabled"} for all ${slideIds.length} slides`,
        );
        return;
      }

      toast.warning(
        `Calculator ${enableAll ? "enabled" : "disabled"} on ${result.updated_count} of ${slideIds.length} slides. Please retry the remaining slides.`,
      );
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to update all slides",
      );
    } finally {
      setIsBulkCalculatorSaving(false);
    }
  };

  // Teacher search for clone/reassign dialogs
  const searchTeachers = useCallback((query: string) => {
    if (teacherDebounceRef.current) clearTimeout(teacherDebounceRef.current);
    if (!query.trim()) {
      setTeacherResults([]);
      return;
    }
    teacherDebounceRef.current = setTimeout(async () => {
      try {
        setTeacherSearchLoading(true);
        const res = await apiService.getTeachersForFilterPage({ query, limit: 10 });
        setTeacherResults(res.items);
      } catch {
        setTeacherResults([]);
      } finally {
        setTeacherSearchLoading(false);
      }
    }, 300);
  }, []);

  const handleClone = async () => {
    if (!lesson) return;
    try {
      setActionLoading(true);
      const res = await apiService.duplicateLesson(
        lesson.lesson_id,
        selectedTeacher?.teacher_id,
      );
      toast.success(
        `Cloned as "${res.lesson_name}" (ID: ${res.new_lesson_id}) and kept ${res.is_published ? "published" : "draft"} state`,
      );
      setCloneOpen(false);
      setSelectedTeacher(null);
      setTeacherQuery("");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to clone lesson",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!lesson) return;
    try {
      setActionLoading(true);
      await apiService.deleteLesson(lesson.lesson_id);
      toast.success(`Lesson "${lesson.lesson_name}" deleted`);
      setDeleteOpen(false);
      setSelectedLessonIdInUrl(null);
      setLesson(null);
      fetchLessons(searchQuery, page, teacherFilter, statusFilter);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to delete lesson",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const describeReassign = (
    res: ReassignLessonResponse,
    teacherName: string,
  ): string => {
    if (res.noop) return `${teacherName} already owns this lesson`;
    const removed = [
      res.pruned.grants && `${res.pruned.grants} grant(s)`,
      res.pruned.classrooms && `${res.pruned.classrooms} classroom assignment(s)`,
      res.pruned.folders && `${res.pruned.folders} folder placement(s)`,
      res.pruned.spaces && `${res.pruned.spaces} space link(s)`,
    ].filter(Boolean);
    const base = `Moved to ${teacherName}`;
    return removed.length ? `${base} — removed ${removed.join(", ")}` : base;
  };

  const handleReassign = async () => {
    if (!lesson || !selectedTeacher) return;
    try {
      setActionLoading(true);
      const res = await apiService.reassignLesson(
        lesson.lesson_id,
        selectedTeacher.teacher_id,
        crossOrgPrompt !== null,
      );
      const teacherName = res.teacher_name || selectedTeacher.name;
      toast.success(describeReassign(res, teacherName));
      setLesson((prev) => (prev ? { ...prev, teacher_name: teacherName } : prev));
      setReassignOpen(false);
      setSelectedTeacher(null);
      setTeacherQuery("");
      setCrossOrgPrompt(null);
      fetchLessons(searchQuery, page, teacherFilter, statusFilter);
    } catch (error) {
      if (error instanceof ReassignError && error.needsCrossOrgConfirm) {
        setCrossOrgPrompt({
          sourceOrg: error.sourceOrg ?? null,
          targetOrg: error.targetOrg ?? null,
        });
      } else {
        toast.error(
          error instanceof Error ? error.message : "Failed to reassign lesson",
        );
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleShare = async () => {
    if (!lesson || !selectedTeacher) return;
    try {
      setActionLoading(true);
      const res = await apiService.grantLesson(
        lesson.lesson_id,
        selectedTeacher.teacher_id,
      );
      toast.success(
        res.created
          ? `Granted access to ${res.teacher_name || selectedTeacher.name}`
          : `Already granted to ${res.teacher_name || selectedTeacher.name}`,
      );
      setShareOpen(false);
      setSelectedTeacher(null);
      setTeacherQuery("");
    } catch {
      toast.error("Failed to grant lesson access");
    } finally {
      setActionLoading(false);
    }
  };

  const handlePreview = async () => {
    if (!lesson) return;
    try {
      setExportLoading(true);
      const data = await apiService.exportLesson(lesson.lesson_id);
      setExportData(data);
      setPreviewOpen(true);
    } catch {
      toast.error("Failed to load lesson content");
    } finally {
      setExportLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!lesson) return;
    try {
      setExportLoading(true);
      const data = await apiService.exportLesson(lesson.lesson_id);
      const json = JSON.stringify(data, null, 2);
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `lesson-${lesson.lesson_id}-${lesson.lesson_name.replace(/[^a-zA-Z0-9]/g, "_")}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch {
      toast.error("Failed to export lesson");
    } finally {
      setExportLoading(false);
    }
  };

  const openTeacherDialog = (dialog: "clone" | "reassign" | "share") => {
    setSelectedTeacher(null);
    setTeacherQuery("");
    setTeacherResults([]);
    setCrossOrgPrompt(null);
    if (dialog === "clone") setCloneOpen(true);
    else if (dialog === "share") setShareOpen(true);
    else setReassignOpen(true);
  };

  const enabledCount = lesson
    ? lesson.slides.filter((s) => isScratchpadEnabled(s.metadata)).length
    : 0;

  const calculatorEnabledCount = lesson
    ? lesson.slides.filter((s) => isCalculatorEnabled(s.metadata)).length
    : 0;

  const totalPages = lessonsPage ? Math.ceil(lessonsPage.total / PAGE_SIZE) : 0;
  const showingFrom = lessonsPage ? lessonsPage.offset + 1 : 0;
  const showingTo = lessonsPage ? Math.min(lessonsPage.offset + lessonsPage.limit, lessonsPage.total) : 0;

  // ── DETAIL VIEW ──
  if (lesson || isLoading) {
    return (
      <div className="flex h-full min-h-0 flex-col gap-4 overflow-y-auto">
        {/* Back button */}
        <Button variant="ghost" size="sm" className="w-fit gap-1.5" onClick={handleBackToList}>
          <ArrowLeft className="size-3.5" /> Back to lessons
        </Button>

        {isLoading && (
          <Card className="rounded-xl border-border/60 p-6 shadow-sm">
            <div className="space-y-3">
              <Skeleton className="h-5 w-48" />
              <Skeleton className="h-4 w-32" />
              <div className="flex gap-2 pt-2">
                <Skeleton className="h-5 w-16 rounded-full" />
                <Skeleton className="h-5 w-20 rounded-full" />
              </div>
            </div>
          </Card>
        )}

        {lesson && !isLoading && (
          <>
            <Card className="rounded-xl border-border/60 p-4 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-semibold">{lesson.lesson_name}</h2>
                  <Badge
                    className={
                      lesson.is_published
                        ? "border border-lime-600/20 bg-lime-600/10 text-lime-900"
                        : "border border-border/60 bg-muted/30 text-foreground"
                    }
                  >
                    {lesson.is_published ? "Published" : "Draft"}
                  </Badge>
                </div>
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                  {lesson.teacher_name && <span>By {lesson.teacher_name}</span>}
                  <span>
                    {lesson.slides.length} slide{lesson.slides.length !== 1 ? "s" : ""}
                  </span>
                  <span>
                    {enabledCount}/{lesson.slides.length} scratchpad enabled
                  </span>
                  <span>
                    {calculatorEnabledCount}/{lesson.slides.length} calculator enabled
                  </span>
                </div>
              </div>
              <LessonActions
                scratchpadBusy={isBulkSaving}
                calculatorBusy={isBulkCalculatorSaving}
                exportBusy={exportLoading}
                onScratchpad={handleToggleAll}
                onCalculator={handleToggleAllCalculator}
                onTeacher={openTeacherDialog}
                onPreview={handlePreview}
                onDownload={handleDownload}
                onDelete={() => setDeleteOpen(true)}
              />
            </div>
          </Card>

          {/* Tutor Version Binding */}
          <Card className="flex flex-wrap items-center gap-3 rounded-xl border-border/60 p-3 shadow-sm">
            <span className="text-sm font-medium whitespace-nowrap">Tutor Version:</span>
            <Select
              value={currentTutorVersionId || "none"}
              onValueChange={(v) => {
                const tutorVersionId = v === "none" ? null : v;
                if (lesson.is_published) {
                  setPendingTutorVersionId(tutorVersionId);
                  setTvConfirmOpen(true);
                } else {
                  handleBindTutorVersion(tutorVersionId);
                }
              }}
              disabled={tvBindingLoading}
            >
              <SelectTrigger className="w-[300px]">
                <SelectValue placeholder="No tutor version assigned" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No tutor version</SelectItem>
                {availableTutorVersions
                  .filter((tv) => tv.status === "published")
                  .map((tv) => (
                    <SelectItem key={tv.tutor_version_id} value={tv.tutor_version_id}>
                      v{tv.version_number} — {tv.name}{tv.org ? ` (${orgIdToName[tv.org] || tv.org})` : ""}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            {currentTutorVersionId && (
              <Badge variant="outline" className="text-xs">
                {availableTutorVersions.find((tv) => tv.tutor_version_id === currentTutorVersionId)?.name || currentTutorVersionId}
              </Badge>
            )}
            {tvBindingLoading && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
          </Card>

          {/* Standards */}
          <LessonStandardsCard lessonId={lesson.lesson_id} />

          {/* Slides Table */}
          <Card className="flex min-h-[320px] shrink-0 flex-1 flex-col overflow-hidden border shadow-sm">
            <div className="border-b border-border/60 px-4 py-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-sm font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                  Slides
                </h2>
                {lesson.is_published && (
                  <span className="text-xs text-muted-foreground">
                    Lesson is published; rows show the student-facing published state.
                  </span>
                )}
              </div>
            </div>
            <div className="relative min-h-0 flex-1 overflow-auto">
              <Table className="min-w-full">
                <TableHeader>
                  <TableRow className="sticky top-0 z-10 border-b border-border/60 bg-card/95 backdrop-blur hover:bg-transparent">
                    <TableHead className="w-16 px-3 text-xs font-semibold uppercase tracking-[0.18em]">
                      #
                    </TableHead>
                    <TableHead className="min-w-[180px] px-3 text-xs font-semibold uppercase tracking-[0.18em]">
                      Name
                    </TableHead>
                    <TableHead className="w-24 px-3 text-xs font-semibold uppercase tracking-[0.18em]">
                      Type
                    </TableHead>
                    <TableHead className="w-24 px-3 text-xs font-semibold uppercase tracking-[0.18em]">
                      Status
                    </TableHead>
                    <TableHead className="w-28 px-3 text-center text-xs font-semibold uppercase tracking-[0.18em]">
                      Scratchpad
                    </TableHead>
                    <TableHead className="w-28 px-3 text-center text-xs font-semibold uppercase tracking-[0.18em]">
                      Calculator
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lesson.slides.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="h-32 text-center text-muted-foreground"
                      >
                        No slides in this lesson
                      </TableCell>
                    </TableRow>
                  ) : (
                    lesson.slides.map((slide) => {
                      const scratchpadEnabled = isScratchpadEnabled(slide.metadata);
                      const isSaving = savingSlideId === slide.id;
                      const calcEnabled = isCalculatorEnabled(slide.metadata);
                      const isCalcSaving = savingCalculatorSlideId === slide.id;

                      return (
                        <TableRow
                          key={slide.id}
                          className="border-b last:border-0 hover:bg-muted/40"
                        >
                          <TableCell className="px-3 py-2 text-sm text-muted-foreground">
                            {slide.slide_index != null
                              ? slide.slide_index + 1
                              : "-"}
                          </TableCell>
                          <TableCell className="px-3 py-2">
                            <span className="text-sm font-medium">
                              {slide.name || "(Untitled)"}
                            </span>
                          </TableCell>
                          <TableCell className="px-3 py-2 text-sm text-muted-foreground">
                            {slide.slide_type || "-"}
                          </TableCell>
                          <TableCell className="px-3 py-2">
                            <Badge
                              className={
                                slide.slide_status === "FINALIZED"
                                  ? "border border-lime-600/20 bg-lime-600/10 text-lime-900 text-xs"
                                  : "border border-border/60 bg-muted/30 text-foreground text-xs"
                              }
                            >
                              {formatSlideStatusLabel(
                                slide.slide_status,
                                lesson.is_published,
                              )}
                            </Badge>
                          </TableCell>
                          <TableCell className="px-3 py-2 text-center">
                            {isSaving ? (
                              <Loader2 className="mx-auto size-4 animate-spin text-muted-foreground" />
                            ) : (
                              <Switch
                                checked={scratchpadEnabled}
                                onCheckedChange={(checked) =>
                                  handleToggleScratchpad(slide, checked)
                                }
                                disabled={isBulkSaving}
                                className="data-[state=checked]:bg-lime-600"
                              />
                            )}
                          </TableCell>
                          <TableCell className="px-3 py-2 text-center">
                            {isCalcSaving ? (
                              <Loader2 className="mx-auto size-4 animate-spin text-muted-foreground" />
                            ) : (
                              <Switch
                                checked={calcEnabled}
                                onCheckedChange={(checked) =>
                                  handleToggleCalculator(slide, checked)
                                }
                                disabled={isBulkCalculatorSaving}
                                className="data-[state=checked]:bg-lime-600"
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>
          </>
        )}

        {/* Dialogs rendered inside detail view */}
        {renderDialogs()}
      </div>
    );
  }

  // ── LIST VIEW ──
  return (
    <div className="flex h-full min-h-0 flex-col gap-4 overflow-y-auto">
      {/* Search */}
      <Card className="rounded-xl border-border/60 p-4 shadow-sm">
        <div className="flex flex-wrap gap-3">
          <div className="relative min-w-[260px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search lessons by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <AdvancedOptions values={{ teacher_id: teacherFilter === "all" ? "" : teacherFilter, organization_id: organizationFilter, status: statusFilter === "all" ? "" : statusFilter }}
            onChange={(values) => { setTeacherFilter(values.teacher_id || "all"); setOrganizationFilter(values.organization_id || ""); setStatusFilter((values.status || "all") as "all" | "published" | "draft"); }}
            fields={[
              { key: "organization_id", label: "Organization", options: allOrgs.map((org) => ({ value: org.id, label: org.name })) },
              { key: "teacher_id", label: "Teacher owner", options: lessonFilterTeachers.map((teacher) => ({ value: String(teacher.teacher_id), label: `${teacher.name || "Teacher"}${teacher.email ? ` <${teacher.email}>` : ""} (#${teacher.teacher_id})` })) },
              { key: "status", label: "Status", options: [{ value: "published", label: "Published" }, { value: "draft", label: "Draft" }] },
            ]} />
        </div>
      </Card>

      {/* Lessons table */}
      <Card className="flex min-h-[320px] shrink-0 flex-1 flex-col overflow-hidden border shadow-sm">
        <div className="relative min-h-0 flex-1 overflow-auto">
          <Table className="min-w-full">
            <TableHeader>
              <TableRow className="sticky top-0 z-10 border-b border-border/60 bg-card/95 backdrop-blur hover:bg-transparent">
                <TableHead className="w-16 px-3 text-xs font-semibold uppercase tracking-[0.18em]">#</TableHead>
                <TableHead className="min-w-[200px] px-3 text-xs font-semibold uppercase tracking-[0.18em]">Name</TableHead>
                <TableHead className="w-32 px-3 text-xs font-semibold uppercase tracking-[0.18em]">Teacher</TableHead>
                <TableHead className="w-20 px-3 text-center text-xs font-semibold uppercase tracking-[0.18em]">Slides</TableHead>
                <TableHead className="w-24 px-3 text-xs font-semibold uppercase tracking-[0.18em]">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {listLoading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell className="px-3 py-2"><Skeleton className="h-4 w-8" /></TableCell>
                    <TableCell className="px-3 py-2"><Skeleton className="h-4 w-40" /></TableCell>
                    <TableCell className="px-3 py-2"><Skeleton className="h-4 w-24" /></TableCell>
                    <TableCell className="px-3 py-2 text-center"><Skeleton className="mx-auto h-4 w-6" /></TableCell>
                    <TableCell className="px-3 py-2"><Skeleton className="h-5 w-16 rounded-full" /></TableCell>
                  </TableRow>
                ))
              ) : !lessonsPage?.items?.length ? (
                <TableRow>
                  <TableCell colSpan={5} className="h-32 text-center text-muted-foreground">
                    {(searchQuery || teacherFilter !== "all" || statusFilter !== "all" || organizationFilter)
                      ? "No lessons match your filters"
                      : "No lessons found"}
                  </TableCell>
                </TableRow>
              ) : (
                lessonsPage.items.map((l) => (
                  <TableRow
                    key={l.lesson_id}
                    className="cursor-pointer border-b last:border-0 hover:bg-muted/40"
                    onClick={() => handleSelectLesson(l)}
                  >
                    <TableCell className="px-3 py-2 text-sm text-muted-foreground">{l.lesson_id}</TableCell>
                    <TableCell className="px-3 py-2">
                      <span className="text-sm font-medium">{l.name}</span>
                    </TableCell>
                    <TableCell className="px-3 py-2 text-sm text-muted-foreground">{l.teacher_name || "-"}</TableCell>
                    <TableCell className="px-3 py-2 text-center text-sm text-muted-foreground">{l.slide_count ?? "-"}</TableCell>
                    <TableCell className="px-3 py-2">
                      <Badge className={
                        l.is_published
                          ? "border border-lime-600/20 bg-lime-600/10 text-lime-900 text-xs"
                          : "border border-border/60 bg-muted/30 text-foreground text-xs"
                      }>
                        {l.is_published ? "Published" : "Draft"}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        {lessonsPage && lessonsPage.total > 0 && (
          <TablePagination
            page={page}
            totalPages={totalPages}
            showingFrom={showingFrom}
            showingTo={showingTo}
            total={lessonsPage.total}
            onPageChange={setPage}
          />
        )}
      </Card>
    </div>
  );

  // ── DIALOGS (extracted to keep JSX manageable) ──
  function renderDialogs() {
    return (
      <>
      {/* Clone Dialog */}
      <Dialog open={cloneOpen} onOpenChange={setCloneOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Clone Lesson</DialogTitle>
            <DialogDescription>
              Create a copy of "{lesson?.lesson_name}". Optionally assign to a different teacher.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <label className="text-sm font-medium">Assign to teacher (optional)</label>
            <Input
              placeholder="Search teacher by name..."
              value={teacherQuery}
              onChange={(e) => {
                setTeacherQuery(e.target.value);
                setSelectedTeacher(null);
                searchTeachers(e.target.value);
              }}
            />
            {teacherSearchLoading && (
              <p className="text-xs text-muted-foreground">Searching...</p>
            )}
            {teacherResults.length > 0 && !selectedTeacher && (
              <ScrollArea className="max-h-[150px] rounded border">
                {teacherResults.map((t) => (
                  <div
                    key={t.teacher_id}
                    className="cursor-pointer px-3 py-2 text-sm hover:bg-muted/50"
                    onClick={() => {
                      setSelectedTeacher(t);
                      setTeacherQuery(t.name);
                      setTeacherResults([]);
                    }}
                  >
                    {t.name} {t.email && <span className="text-xs text-muted-foreground">({t.email})</span>}
                  </div>
                ))}
              </ScrollArea>
            )}
            {selectedTeacher && (
              <p className="text-sm text-muted-foreground">
                Will clone to: <strong>{selectedTeacher.name}</strong>
              </p>
            )}
            {!selectedTeacher && !teacherQuery && (
              <p className="text-xs text-muted-foreground">
                Leave empty to clone under the same teacher.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloneOpen(false)}>Cancel</Button>
            <Button onClick={handleClone} disabled={actionLoading} className="gap-1.5 bg-lime-600/90 text-white hover:bg-lime-600">
              {actionLoading ? <Loader2 className="size-3.5 animate-spin" /> : <Copy className="size-3.5" />}
              Clone
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Dialog — grants access without changing owner */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Grant Lesson Access</DialogTitle>
            <DialogDescription>
              Grant a teacher access to "{lesson?.lesson_name}" without changing
              the owner
              {lesson?.teacher_name && <> (<strong>{lesson.teacher_name}</strong>)</>}
              . This creates the same access grant a teacher would get if an org
              admin assigned the lesson to them — it appears as assigned to that
              teacher in the studio, while the owner stays unchanged.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <label className="text-sm font-medium">Teacher</label>
            <Input
              placeholder="Search teacher by name..."
              value={teacherQuery}
              onChange={(e) => {
                setTeacherQuery(e.target.value);
                setSelectedTeacher(null);
                searchTeachers(e.target.value);
              }}
            />
            {teacherSearchLoading && (
              <p className="text-xs text-muted-foreground">Searching...</p>
            )}
            {teacherResults.length > 0 && !selectedTeacher && (
              <ScrollArea className="max-h-[150px] rounded border">
                {teacherResults.map((t) => (
                  <div
                    key={t.teacher_id}
                    className="cursor-pointer px-3 py-2 text-sm hover:bg-muted/50"
                    onClick={() => {
                      setSelectedTeacher(t);
                      setTeacherQuery(t.name);
                      setTeacherResults([]);
                    }}
                  >
                    {t.name} {t.email && <span className="text-xs text-muted-foreground">({t.email})</span>}
                  </div>
                ))}
              </ScrollArea>
            )}
            {selectedTeacher && (
              <p className="text-sm text-muted-foreground">
                Will grant access to: <strong>{selectedTeacher.name}</strong>
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShareOpen(false)}>Cancel</Button>
            <Button onClick={handleShare} disabled={actionLoading || !selectedTeacher} className="gap-1.5 bg-lime-600/90 text-white hover:bg-lime-600">
              {actionLoading ? <Loader2 className="size-3.5 animate-spin" /> : <UserPlus className="size-3.5" />}
              Grant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reassign Dialog */}
      <Dialog
        open={reassignOpen}
        onOpenChange={(open) => {
          setReassignOpen(open);
          if (!open) setCrossOrgPrompt(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Move Lesson</DialogTitle>
            <DialogDescription>
              Transfer ownership of "{lesson?.lesson_name}" to another teacher.
              {lesson?.teacher_name && <> Currently owned by <strong>{lesson.teacher_name}</strong>.</>}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <label className="text-sm font-medium">New owner</label>
            <Input
              placeholder="Search teacher by name..."
              value={teacherQuery}
              onChange={(e) => {
                setTeacherQuery(e.target.value);
                setSelectedTeacher(null);
                setCrossOrgPrompt(null);
                searchTeachers(e.target.value);
              }}
            />
            {teacherSearchLoading && (
              <p className="text-xs text-muted-foreground">Searching...</p>
            )}
            {teacherResults.length > 0 && !selectedTeacher && (
              <ScrollArea className="max-h-[150px] rounded border">
                {teacherResults.map((t) => (
                  <div
                    key={t.teacher_id}
                    className="cursor-pointer px-3 py-2 text-sm hover:bg-muted/50"
                    onClick={() => {
                      setSelectedTeacher(t);
                      setTeacherQuery(t.name);
                      setTeacherResults([]);
                      setCrossOrgPrompt(null);
                    }}
                  >
                    {t.name} {t.email && <span className="text-xs text-muted-foreground">({t.email})</span>}
                  </div>
                ))}
              </ScrollArea>
            )}
            {selectedTeacher && !crossOrgPrompt && (
              <p className="text-sm text-muted-foreground">
                Will move to: <strong>{selectedTeacher.name}</strong>
              </p>
            )}
            {crossOrgPrompt && (
              <div className="flex gap-2 rounded border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
                <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" />
                <div className="space-y-1">
                  <p>
                    This moves the lesson from{" "}
                    <strong>{crossOrgPrompt.sourceOrg ?? "no organization"}</strong> to{" "}
                    <strong>{crossOrgPrompt.targetOrg ?? "no organization"}</strong>.
                  </p>
                  <p className="text-muted-foreground">
                    Grants, folder placement and classroom assignments in the old
                    organization will be removed.
                  </p>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassignOpen(false)}>Cancel</Button>
            <Button
              onClick={handleReassign}
              disabled={actionLoading || !selectedTeacher}
              className={
                crossOrgPrompt
                  ? "gap-1.5 bg-amber-600/90 text-white hover:bg-amber-600"
                  : "gap-1.5 bg-lime-600/90 text-white hover:bg-lime-600"
              }
            >
              {actionLoading ? <Loader2 className="size-3.5 animate-spin" /> : <UserRoundCog className="size-3.5" />}
              {crossOrgPrompt ? "Move across organizations" : "Move"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Lesson</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{lesson?.lesson_name}"?
              This will soft-delete the lesson and its {lesson?.slides.length} slide{lesson?.slides.length !== 1 ? "s" : ""}.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button
              onClick={handleDelete}
              disabled={actionLoading}
              className="gap-1.5 bg-red-600 text-white hover:bg-red-700"
            >
              {actionLoading ? <Loader2 className="size-3.5 animate-spin" /> : <Trash2 className="size-3.5" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tutor Version Reassign Confirmation Dialog */}
      <Dialog open={tvConfirmOpen} onOpenChange={setTvConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reassign Tutor Version</DialogTitle>
            <DialogDescription>
              This lesson is published. Changing the tutor version will take effect immediately for active sessions. Are you sure?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setTvConfirmOpen(false); setPendingTutorVersionId(null); }}>Cancel</Button>
            <Button
              onClick={() => { setTvConfirmOpen(false); handleBindTutorVersion(pendingTutorVersionId); setPendingTutorVersionId(null); }}
              className="gap-1.5 bg-lime-600/90 text-white hover:bg-lime-600"
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="flex h-[80vh] max-h-[80vh] flex-col overflow-hidden sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>Lesson Preview: {exportData?.lesson.name}</DialogTitle>
            <DialogDescription>
              {exportData?.slides.length} slides
              {exportData?.lesson.teacher_name && <> &middot; By {exportData.lesson.teacher_name}</>}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="min-h-0 flex-1">
            <div className="space-y-4 pr-4">
              {exportData?.slides.map((slide, i) => {
                const previewBlocks = getSlidePreviewBlocks(slide);
                const previewImages = getSlidePreviewImages(slide);
                return (
                <div key={slide.id} className="rounded-lg border p-4">
                  <div className="mb-3 flex items-center gap-2">
                    <Badge className="border border-border/60 bg-muted/30 text-foreground text-xs">
                      Slide {(slide.slide_index ?? i) + 1}
                    </Badge>
                    <span className="text-sm font-medium">{slide.name || "(Untitled)"}</span>
                    {slide.slide_type && (
                      <span className="text-xs text-muted-foreground">{slide.slide_type}</span>
                    )}
                  </div>
                  <div className="rounded-lg border border-border/60 bg-muted/20 p-4">
                    {previewImages.length > 0 && (
                      <div className="mb-4 grid gap-3 sm:grid-cols-2">
                        {previewImages.map((image) => (
                          <figure
                            key={`${slide.id}-image-${image.url}`}
                            className="overflow-hidden rounded-md border bg-background"
                          >
                            <img
                              src={image.url}
                              alt={image.alt}
                              className="max-h-72 w-full object-contain"
                              loading="lazy"
                            />
                          </figure>
                        ))}
                      </div>
                    )}
                    {previewBlocks.length > 0 ? (
                      <div className="space-y-2">
                        {previewBlocks.map((block, blockIndex) => (
                          <p
                            key={`${slide.id}-preview-${blockIndex}`}
                            className="text-sm leading-6 text-foreground"
                          >
                            {block}
                          </p>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        No readable preview text found for this slide.
                      </p>
                    )}
                  </div>
                  <details className="mt-3">
                    <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
                      Developer details
                    </summary>
                    <pre className="mt-1 max-h-[260px] overflow-auto rounded bg-muted/30 p-2 text-xs">
                      {JSON.stringify(
                        {
                          content: slide.content,
                          flow: slide.flow,
                          metadata: slide.metadata,
                        },
                        null,
                        2,
                      )}
                    </pre>
                  </details>
                </div>
                );
              })}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
      </>
    );
  }
};
